import React, { useState } from "react";
import { useAuth } from "../Context/AuthContext";
import "../Styling/ProjectProgressDashboard.css";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Base_URL } from "../url";
import { useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import SearchableMultiSelect from "../Pages/ProjectComponents/SearchableMultiSelect";

const ProjectProgressList = () => {
  const { user } = useAuth();
  const location = useLocation();

  const [filters, setFilters] = useState({
    type: "All",
    client: [],
    project: [],
    resource: [],
    fromDate: "",
    toDate: "",
    dueDate: "",
    dueStatus: [],
    status: [],
    changedFromDate: "",
    changedToDate: "",
    submittedBy: [],
    active: "All",
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [dropdownData, setDropdownData] = useState({
    clients: [],
    projects: [],
    types: [],
    resources: [],
    submittedBy: [],
    dueStatuses: [],
    statuses: [],
  });

  // SORT STATE
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  const handleSortToggle = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  // Derived sorted data
  const sortedData = React.useMemo(() => {
    if (!sortField) return tableData;
    return [...tableData].sort((a, b) => {
      const dateA = a[sortField] ? new Date(a[sortField]) : new Date(0);
      const dateB = b[sortField] ? new Date(b[sortField]) : new Date(0);
      return sortDir === "desc" ? dateB - dateA : dateA - dateB;
    });
  }, [tableData, sortField, sortDir]);

  const sortArrow = (field) => {
    if (sortField !== field) return " ↕";
    return sortDir === "desc" ? " ▼" : " ▲";
  };

  const handleDownloadExcel = () => {
    if (!tableData || tableData.length === 0) {
      alert("No data to export");
      return;
    }

    const formattedData = tableData.map((row) => ({
      "Active Status": row.active ? "Active" : "Inactive",
      "Org Name": row.orgDisplayName || "Not specified",
      "Client Name": row.clientDisplayName || "Not specified",
      "Project ID": row.projectId || "Not specified",
      "Project Name": row.projectName || "Not specified",
      "Project Manager": row.projectManagerName || "Not specified",
      "Assigned To": row.resourceId || "Not specified",
      "Due Date": row.dueDate ? new Date(row.dueDate).toLocaleDateString() : "Not specified",
      "Due Status": row.dueStatus || "Not specified",
      "Created On": row.createdOn
        ? new Date(row.createdOn).toLocaleDateString()
        : "Not specified",
      "Changed On": row.chargedOn
        ? new Date(row.chargedOn).toLocaleDateString()
        : "Not specified",
      "Status": row.status || "Not specified",
      "Priority": row.priority || "Not specified",
      "Ticket/Task ID": row.ticketId || "Not specified",
      "Title": row.title || "Not specified",
      "Type": row.type || "Not specified",
      "Submitted By": row.submittedByName || row.submittedBy || "Not specified",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const columnWidths = Object.keys(formattedData[0]).map((key) => {
      const maxLength = Math.max(
        key.length,
        ...formattedData.map((row) =>
          row[key] ? row[key].toString().length : 0,
        ),
      );
      return { wch: maxLength + 2 };
    });

    worksheet["!cols"] = columnWidths;
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Project Progress");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, "Project_Progress_List.xlsx");
  };

  const handleChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApply = async (isManual = true) => {
    console.log("Loading started");
    setIsLoading(true);

    try {
      // Prepare filters for API - convert arrays to comma-separated strings
      const apiFilters = {};

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "All") {
          if (Array.isArray(value) && value.length > 0) {
            apiFilters[key] = value.join(",");
          } else if (!Array.isArray(value) && value !== "" && value !== "All") {
            apiFilters[key] = value;
          }
        }
      });

      if (isManual) {
        const params = new URLSearchParams();
        Object.entries(apiFilters).forEach(([key, val]) => {
          params.set(key, val);
        });
        window.history.replaceState(null, "", `?${params.toString()}`);
      }

      const res = await axios.get(`${Base_URL}/ProjectProgress/list`, {
        params: {
          ...apiFilters,
          orgId: user?.organisationId,
        },
      });

      console.log("API response:", res);

      const data = res?.data?.data || res?.data?.Data || res?.data || [];

      const mappedData = (Array.isArray(data) ? data : []).map((item) => ({
        ...item,
        status: item.status ?? item.Status ?? item.STATUS ?? "",
        orgDisplayName:
          item.orgShortName && item.orgShortName.trim()
            ? item.orgShortName
            : item.orgName,
        clientDisplayName: item.clientShortName || "",
      }));

      // Apply frontend filters for multi-select values
      let filteredData = mappedData;

      // Filter by multiple clients
      if (filters.client && filters.client.length > 0) {
        filteredData = filteredData.filter((item) =>
          filters.client.includes(item.clientDisplayName),
        );
      }

      // Filter by multiple projects
      if (filters.project && filters.project.length > 0) {
        filteredData = filteredData.filter((item) =>
          filters.project.includes(item.projectName),
        );
      }

      // Filter by multiple resources
      if (filters.resource && filters.resource.length > 0) {
        filteredData = filteredData.filter((item) =>
          filters.resource.includes(item.resourceId),
        );
      }

      // Filter by multiple due statuses
      if (filters.dueStatus && filters.dueStatus.length > 0) {
        filteredData = filteredData.filter((item) =>
          filters.dueStatus.includes(item.dueStatus),
        );
      }

      // Filter by multiple statuses
      if (filters.status && filters.status.length > 0) {
        filteredData = filteredData.filter((item) =>
          filters.status.includes(item.status),
        );
      }

      // Filter by multiple submitted by
      if (filters.submittedBy && filters.submittedBy.length > 0) {
        filteredData = filteredData.filter((item) =>
          filters.submittedBy.includes(item.submittedBy),
        );
      }

      if (filters.active && filters.active !== "All") {
        const isActive = filters.active === "Active";
        filteredData = filteredData.filter((item) => item.active === isActive);
      }

      // Date filters
      filteredData = filteredData.filter((item) => {
        const chargedDate = new Date(item.chargedOn);
        const createdDate = new Date(item.createdOn);

        if (filters.fromDate && createdDate < new Date(filters.fromDate))
          return false;
        if (filters.toDate && createdDate > new Date(filters.toDate))
          return false;
        if (
          filters.changedFromDate &&
          chargedDate < new Date(filters.changedFromDate)
        )
          return false;
        if (
          filters.changedToDate &&
          chargedDate > new Date(filters.changedToDate)
        )
          return false;

        if (
          filters.dueDate &&
          new Date(item.dueDate) < new Date(filters.dueDate)
        )
          return false;

        return true;
      });

      setTableData(filteredData);
    } catch (err) {
      console.log("List API error:", err);
    } finally {
      setTimeout(() => {
        console.log("Loading ended");
        setIsLoading(false);
      }, 300);
    }
  };

  const handleClear = () => {
    const resetFilters = {
      type: "All",
      client: [],
      project: [],
      resource: [],
      fromDate: "",
      toDate: "",
      dueDate: "",
      dueStatus: [],
      status: [],
      changedFromDate: "",
      changedToDate: "",
      submittedBy: [],
      active: "All",
    };

    setFilters(resetFilters);
    setTimeout(() => handleApply(true), 100);
  };

  // Load filters from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newFilters = {
      type: "All",
      client: [],
      project: [],
      resource: [],
      fromDate: "",
      toDate: "",
      dueDate: "",
      dueStatus: [],
      status: [],
      changedFromDate: "",
      changedToDate: "",
      submittedBy: [],
      active: "All",
    };

    for (let [key, value] of params.entries()) {
      if (
        [
          "client",
          "project",
          "resource",
          "dueStatus",
          "status",
          "submittedBy",
        ].includes(key)
      ) {
        newFilters[key] = value.split(",");
      } else if (key === "active") {
        newFilters[key] = value;
      } else {
        newFilters[key] = value;
      }
    }

    setFilters(newFilters);
    setIsInitialized(true);
  }, [location.search]);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const res = await axios.get(`${Base_URL}/ProjectProgress/dropdowns`, {
          params: { orgId: user?.organisationId },
        });

        const data = res?.data?.data || res?.data?.Data || res?.data || {};

        setDropdownData({
          clients: data.client ?? [],
          projects: data.projects ?? [],
          types: data.types ?? [],
          resources: data.resources ?? [],
          submittedBy: data.submittedBy ?? [],
          dueStatuses: data.dueStatuses ?? [],
          statuses: data.statuses ?? [],
        });
      } catch (err) {
        console.error("Dropdown API error:", err);
      }
    };

    if (user?.organisationId) {
      fetchDropdowns();
    }
  }, [user]);

  // Initial data load
  useEffect(() => {
    if (user?.organisationId && isInitialized) {
      handleApply(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organisationId, isInitialized]);

  return (
    <div className="ppl-container">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <h2 className="ppl-title" style={{ margin: 0 }}>
          Project Progress List
        </h2>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => setShowFilters((prev) => !prev)}
            style={{
              backgroundColor: "#687c9a",
              color: "#fff",
              border: "none",
              padding: "8px 12px",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "all 0.25s ease",
              boxShadow: "0 4px 12px rgba(124, 58, 237, 0.25)",
              fontSize: "13px",
              fontWeight: "500",
              minWidth: "140px",
            }}
          >
            {showFilters ? "Hide Filters ▲" : "Show Filters ▼"}
          </button>

          <button onClick={handleDownloadExcel} className="download-btn">
            ⬇ Download Excel
          </button>
        </div>
      </div>

      {/* FILTERS */}
      {showFilters && (
        <div className="ppd-filter-box">
          <div className="ppd-field">
            <label>Organisation ID</label>
            <input value={user?.organisationId || ""} disabled />
          </div>

          <div className="ppd-field">
            <label>Type</label>
            <select
              value={filters.type}
              onChange={(e) => handleChange("type", e.target.value)}
              key={dropdownData.types.join(",")}
            >
              <option value="All">All</option>
              {(dropdownData.types || []).map((t, i) => (
                <option key={i} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="ppd-field">
            <label>Client</label>
            <SearchableMultiSelect
              value={filters.client}
              onChange={(value) => handleChange("client", value)}
              options={dropdownData.clients || []}
              placeholder="Select Clients..."
              searchPlaceholder="Search clients..."
              noResultsText="No clients found"
            />
          </div>

          <div className="ppd-field">
            <label>Project</label>
            <SearchableMultiSelect
              value={filters.project}
              onChange={(value) => handleChange("project", value)}
              options={dropdownData.projects || []}
              placeholder="Select Projects..."
              searchPlaceholder="Search projects..."
              noResultsText="No projects found"
            />
          </div>

          <div className="ppd-field">
            <label>Assigned To</label>
            <SearchableMultiSelect
              value={filters.resource}
              onChange={(value) => handleChange("resource", value)}
              options={dropdownData.resources || []}
              placeholder="Select Resources..."
              searchPlaceholder="Search resources..."
              noResultsText="No resources found"
            />
          </div>

          <div className="ppd-field">
            <label>Due Date</label>
            <input
              type="date"
              value={filters.dueDate}
              onChange={(e) => handleChange("dueDate", e.target.value)}
            />
          </div>

          <div className="ppd-field">
            <label>Submitted By</label>
            <SearchableMultiSelect
              value={filters.submittedBy}
              onChange={(value) => handleChange("submittedBy", value)}
              options={dropdownData.submittedBy || []}
              placeholder="Select Users..."
              searchPlaceholder="Search users..."
              noResultsText="No users found"
            />
          </div>

          <div className="ppd-field">
            <label>Due Status</label>
            <SearchableMultiSelect
              value={filters.dueStatus}
              onChange={(value) => handleChange("dueStatus", value)}
              options={dropdownData.dueStatuses || []}
              placeholder="Select Due Statuses..."
              searchPlaceholder="Search statuses..."
              noResultsText="No statuses found"
            />
          </div>

          <div className="ppd-field">
            <label>Status</label>
            <SearchableMultiSelect
              value={filters.status}
              onChange={(value) => handleChange("status", value)}
              options={dropdownData.statuses || []}
              placeholder="Select Statuses..."
              searchPlaceholder="Search statuses..."
              noResultsText="No statuses found"
            />
          </div>

          <div className="ppd-field">
            <label>Active Status</label>
            <select
              value={filters.active}
              onChange={(e) => handleChange("active", e.target.value)}
            >
              <option value="All">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div className="ppd-date-row">
            <div className="ppd-field">
              <label>Created From Date</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleChange("fromDate", e.target.value)}
              />
            </div>

            <div className="ppd-field">
              <label>Created To Date</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => handleChange("toDate", e.target.value)}
              />
            </div>
          </div>

          <div className="ppd-date-row">
            <div className="ppd-field">
              <label>Changed From Date</label>
              <input
                type="date"
                value={filters.changedFromDate}
                onChange={(e) => handleChange("changedFromDate", e.target.value)}
              />
            </div>

            <div className="ppd-field">
              <label>Changed To Date</label>
              <input
                type="date"
                value={filters.changedToDate}
                onChange={(e) => handleChange("changedToDate", e.target.value)}
              />
            </div>
          </div>

          <div className="ppd-buttons">
            <button className="apply-btn" onClick={() => handleApply(true)}>
              Apply Filters
            </button>
            <button className="clear-btn" onClick={handleClear}>
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* TABLE */}
      <div className="ppl-table-container">
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <div
              style={{
                width: "50px",
                height: "50px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #687c9a",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            ></div>
            <div style={{ color: "#687c9a", fontSize: "16px", fontWeight: "500" }}>
              Loading project progress data...
            </div>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        ) : (
          <table className="ppl-table">
            <thead>
              <tr>
                <th>Active</th>
                <th>Client Name</th>
                <th>Project ID</th>
                <th style={{ width: "180px" }}>Project Name</th>
                <th>Project Manager</th>
                <th>Assigned To</th>
                <th>Due Date</th>
                <th>Due Status</th>
                <th
                  onClick={() => handleSortToggle("createdOn")}
                  style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                  title="Click to sort by Created Date"
                >
                  Created On{sortArrow("createdOn")}
                </th>
                <th
                  onClick={() => handleSortToggle("chargedOn")}
                  style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                  title="Click to sort by Changed Date"
                >
                  Changed On{sortArrow("chargedOn")}
                </th>
                <th>Status</th>
                <th>Priority</th>
                <th>Ticket/Task ID</th>
                <th style={{ width: "220px" }}>Title</th>
                <th>Type</th>
                <th>Submitted By</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.length > 0 ? (
                sortedData.map((row, index) => (
                  <tr key={index}>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: "600",
                          backgroundColor: row.active ? "#4caf50" : "#f44336",
                          color: "#fff",
                          textAlign: "center",
                          minWidth: "60px",
                        }}
                      >
                        {row.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{row.clientDisplayName || "Not specified"}</td>
                    <td>{row.projectId || "Not specified"}</td>
                    <td
                      style={{
                        maxWidth: "180px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.projectId ? (
                        <a
                          href={`/project/${row.projectId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Click to view project details"
                          style={{
                            color: "#03275c",
                            textDecoration: "none",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
                          onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                        >
                          {row.projectName || row.projectId || "Not specified"}
                        </a>
                      ) : (
                        "Not specified"
                      )}
                    </td>
                    <td>{row.projectManagerName || "Not specified"}</td>
                    <td>{row.resourceId || "Not specified"}</td>
                    <td>
                      {row.dueDate
                        ? new Date(row.dueDate).toLocaleDateString()
                        : "Not specified"}
                    </td>
                    <td>{row.dueStatus || "Not specified"}</td>
                    <td>
                      {row.createdOn
                        ? new Date(row.createdOn).toLocaleDateString()
                        : "Not specified"}
                    </td>
                    <td>
                      {row.chargedOn
                        ? new Date(row.chargedOn).toLocaleDateString()
                        : "Not specified"}
                    </td>
                    <td>{row.status || "Not specified"}</td>
                    <td>{row.priority || "Not specified"}</td>
                    <td>
                      {row.ticketId ? (
                        row.type?.toLowerCase() === "task" ? (
                          <a
                            href={`/project/${row.projectId}/task/${row.ticketId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Click to view task details"
                            style={{
                              color: "#03275c",
                              textDecoration: "none",
                              cursor: "pointer",
                            }}
                            onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
                            onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                          >
                            {row.ticketId}
                          </a>
                        ) : (
                          <Link
                            to={`/ticketinfo/${row.ticketId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Click to view ticket details"
                            style={{
                              color: "#03275c",
                              textDecoration: "none",
                              cursor: "pointer",
                            }}
                            onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
                            onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                          >
                            {row.ticketId}
                          </Link>
                        )
                      ) : (
                        "Not specified"
                      )}
                    </td>
                    <td
                      style={{
                        maxWidth: "220px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={row.title}
                    >
                      {row.title || "Not specified"}
                    </td>
                    <td>{row.type || "Not specified"}</td>
                    <td>{row.submittedByName || "Not specified"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="17" style={{ textAlign: "center", padding: "40px" }}>
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ProjectProgressList;

// import React, { useState } from "react";
// import { useAuth } from "../Context/AuthContext";
// import "../Styling/ProjectProgressDashboard.css";
// import axios from "axios";
// import { Link, useLocation } from "react-router-dom";
// import { Base_URL } from "../url";
// import { useEffect } from "react";
// import * as XLSX from "xlsx";
// import { saveAs } from "file-saver";
// import SearchableMultiSelect from "../Pages/ProjectComponents/SearchableMultiSelect";
// import SearchableSelect from "../Pages/ProjectComponents/SearchableSelect";

// const ProjectProgressList = () => {
//   const { user } = useAuth();
//   const location = useLocation();

//   const [filters, setFilters] = useState({
//     type: "All",
//     client: [],
//     project: [],
//     resource: [],
//     fromDate: "",
//     toDate: "",
//     dueDate: "",
//     dueStatus: [],
//     status: [],
//     changedFromDate: "",
//     changedToDate: "",
//     submittedBy: [],
//     active: "All",
//   });

//   const [isInitialized, setIsInitialized] = useState(false);
//   const [tableData, setTableData] = useState([]);
//   const [showFilters, setShowFilters] = useState(true);
//   const [isLoading, setIsLoading] = useState(true); // Start with true to show loader on initial load
//   const [dropdownData, setDropdownData] = useState({
//     clients: [],
//     projects: [],
//     types: [],
//     resources: [],
//     submittedBy: [],
//     dueStatuses: [],
//     statuses: [],
//   });

//   // SORT STATE
//   const [sortField, setSortField] = useState(null);
//   const [sortDir, setSortDir] = useState("desc");

//   const handleSortToggle = (field) => {
//     if (sortField === field) {
//       setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
//     } else {
//       setSortField(field);
//       setSortDir("desc");
//     }
//   };

//   // Derived sorted data
//   const sortedData = React.useMemo(() => {
//     if (!sortField) return tableData;
//     return [...tableData].sort((a, b) => {
//       const dateA = a[sortField] ? new Date(a[sortField]) : new Date(0);
//       const dateB = b[sortField] ? new Date(b[sortField]) : new Date(0);
//       return sortDir === "desc" ? dateB - dateA : dateA - dateB;
//     });
//   }, [tableData, sortField, sortDir]);

//   const sortArrow = (field) => {
//     if (sortField !== field) return " ↕";
//     return sortDir === "desc" ? " ▼" : " ▲";
//   };

//   const handleDownloadExcel = () => {
//   if (!tableData || tableData.length === 0) {
//     alert("No data to export");
//     return;
//   }

//   const formattedData = tableData.map((row) => ({
//     "Active Status": row.active ? "Active" : "Inactive",
//     "Org Name": row.orgDisplayName || "Not specified",
//     "Client Name": row.clientDisplayName || "Not specified",
//     "Project ID": row.projectId || "Not specified",
//     "Project Name": row.projectName || "Not specified",
//     "Project Manager": row.projectManagerName || "Not specified",
//     Resource: row.resourceId || "Not specified",
//     "Due Date": row.dueDate ? new Date(row.dueDate).toLocaleDateString() : "Not specified",
//     "Due Status": row.dueStatus || "Not specified",
//     "Created On": row.createdOn
//       ? new Date(row.createdOn).toLocaleDateString()
//       : "Not specified",
//     "Changed On": row.chargedOn
//       ? new Date(row.chargedOn).toLocaleDateString()
//       : "Not specified",
//     Status: row.status || "Not specified",
//     Priority: row.priority || "Not specified",
//     "Ticket/Task ID": row.ticketId || "Not specified",
//     Title: row.title || "Not specified",
//     Type: row.type || "Not specified",
//     "Submitted By": row.submittedByName || row.submittedBy || "Not specified",
//   }));

//   const worksheet = XLSX.utils.json_to_sheet(formattedData);
//   const columnWidths = Object.keys(formattedData[0]).map((key) => {
//     const maxLength = Math.max(
//       key.length,
//       ...formattedData.map((row) =>
//         row[key] ? row[key].toString().length : 0,
//       ),
//     );
//     return { wch: maxLength + 2 };
//   });

//   worksheet["!cols"] = columnWidths;
//   const workbook = XLSX.utils.book_new();
//   XLSX.utils.book_append_sheet(workbook, worksheet, "Project Progress");
//   const excelBuffer = XLSX.write(workbook, {
//     bookType: "xlsx",
//     type: "array",
//   });
//   const blob = new Blob([excelBuffer], {
//     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
//   });
//   saveAs(blob, "Project_Progress_List.xlsx");
//   };
  
//   const handleChange = (field, value) => {
//     setFilters((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const handleApply = async (isManual = true) => {
//     console.log("Loading started");
//     setIsLoading(true);

//     try {
//       // Prepare filters for API - convert arrays to comma-separated strings
//       const apiFilters = {};

//       Object.entries(filters).forEach(([key, value]) => {
//         if (value && value !== "All") {
//           if (Array.isArray(value) && value.length > 0) {
//             apiFilters[key] = value.join(",");
//           } else if (!Array.isArray(value) && value !== "" && value !== "All") {
//             apiFilters[key] = value;
//           }
//         }
//       });

//       if (isManual) {
//         const params = new URLSearchParams();
//         Object.entries(apiFilters).forEach(([key, val]) => {
//           params.set(key, val);
//         });
//         window.history.replaceState(null, "", `?${params.toString()}`);
//       }

//       const res = await axios.get(`${Base_URL}/ProjectProgress/list`, {
//         params: {
//           ...apiFilters,
//           orgId: user?.organisationId,
//         },
//       });

//       console.log("API response:", res);

//       const data = res?.data?.data || res?.data?.Data || res?.data || [];

//       const mappedData = (Array.isArray(data) ? data : []).map((item) => ({
//         ...item,
//         status: item.status ?? item.Status ?? item.STATUS ?? "",
//         orgDisplayName:
//           item.orgShortName && item.orgShortName.trim()
//             ? item.orgShortName
//             : item.orgName,
//         clientDisplayName: item.clientShortName || "",
//       }));

//       // Apply frontend filters for multi-select values
//       let filteredData = mappedData;

//       // Filter by multiple clients
//       if (filters.client && filters.client.length > 0) {
//         filteredData = filteredData.filter((item) =>
//           filters.client.includes(item.clientDisplayName),
//         );
//       }

//       // Filter by multiple projects
//       if (filters.project && filters.project.length > 0) {
//         filteredData = filteredData.filter((item) =>
//           filters.project.includes(item.projectName),
//         );
//       }

//       // Filter by multiple resources
//       if (filters.resource && filters.resource.length > 0) {
//         filteredData = filteredData.filter((item) =>
//           filters.resource.includes(item.resourceId),
//         );
//       }

//       // Filter by multiple due statuses
//       if (filters.dueStatus && filters.dueStatus.length > 0) {
//         filteredData = filteredData.filter((item) =>
//           filters.dueStatus.includes(item.dueStatus),
//         );
//       }

//       // Filter by multiple statuses
//       if (filters.status && filters.status.length > 0) {
//         filteredData = filteredData.filter((item) =>
//           filters.status.includes(item.status),
//         );
//       }

//       // Filter by multiple submitted by
//       if (filters.submittedBy && filters.submittedBy.length > 0) {
//         filteredData = filteredData.filter((item) =>
//           filters.submittedBy.includes(item.submittedBy),
//         );
//       }

//       if (filters.active && filters.active !== "All") {
//         const isActive = filters.active === "Active";
//         filteredData = filteredData.filter((item) => item.active === isActive);
//       }

//       // Date filters
//       filteredData = filteredData.filter((item) => {
//         const chargedDate = new Date(item.chargedOn);
//         const createdDate = new Date(item.createdOn);

//         if (filters.fromDate && createdDate < new Date(filters.fromDate))
//           return false;
//         if (filters.toDate && createdDate > new Date(filters.toDate))
//           return false;
//         if (
//           filters.changedFromDate &&
//           chargedDate < new Date(filters.changedFromDate)
//         )
//           return false;
//         if (
//           filters.changedToDate &&
//           chargedDate > new Date(filters.changedToDate)
//         )
//           return false;

//         if (
//           filters.dueDate &&
//           new Date(item.dueDate) < new Date(filters.dueDate)
//         )
//           return false;

//         return true;
//       });

//       setTableData(filteredData);
//     } catch (err) {
//       console.log("List API error:", err);
//     } finally {
//       // Add a small delay to ensure loader is visible
//       setTimeout(() => {
//         console.log("Loading ended");
//         setIsLoading(false);
//       }, 300);
//     }
//   };

//   const handleClear = () => {
//     const resetFilters = {
//       type: "All",
//       client: [],
//       project: [],
//       resource: [],
//       fromDate: "",
//       toDate: "",
//       dueDate: "",
//       dueStatus: [],
//       status: [],
//       changedFromDate: "",
//       changedToDate: "",
//       submittedBy: [],
//       active: "All",
//     };

//     setFilters(resetFilters);
//     setTimeout(() => handleApply(true), 100);
//   };

//   // Load filters from URL
//   useEffect(() => {
//     const params = new URLSearchParams(location.search);
//     const newFilters = {
//       type: "All",
//       client: [],
//       project: [],
//       resource: [],
//       fromDate: "",
//       toDate: "",
//       dueDate: "",
//       dueStatus: [],
//       status: [],
//       changedFromDate: "",
//       changedToDate: "",
//       submittedBy: [],
//       active: "All",
//     };

//     for (let [key, value] of params.entries()) {
//       if (
//         [
//           "client",
//           "project",
//           "resource",
//           "dueStatus",
//           "status",
//           "submittedBy",
//         ].includes(key)
//       ) {
//         newFilters[key] = value.split(",");
//       } else if (key === "active") {
//         newFilters[key] = value;
//       } else {
//         newFilters[key] = value;
//       }
//     }

//     setFilters(newFilters);
//     setIsInitialized(true);
//   }, [location.search]);

//   // Fetch dropdown data
//   useEffect(() => {
//     const fetchDropdowns = async () => {
//       try {
//         const res = await axios.get(`${Base_URL}/ProjectProgress/dropdowns`, {
//           params: { orgId: user?.organisationId },
//         });

//         const data = res?.data?.data || res?.data?.Data || res?.data || {};

//         setDropdownData({
//           clients: data.client ?? [],
//           projects: data.projects ?? [],
//           types: data.types ?? [],
//           resources: data.resources ?? [],
//           submittedBy: data.submittedBy ?? [],
//           dueStatuses: data.dueStatuses ?? [],
//           statuses: data.statuses ?? [],
//         });
//       } catch (err) {
//         console.error("Dropdown API error:", err);
//       }
//     };

//     if (user?.organisationId) {
//       fetchDropdowns();
//     }
//   }, [user]);

//   // Initial data load - only once when component mounts
//   useEffect(() => {
//     if (user?.organisationId && isInitialized) {
//       handleApply(false);
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [user?.organisationId, isInitialized]); // Remove filters dependency

//   return (
//     <div className="ppl-container">
//       <div
//         style={{
//           display: "flex",
//           justifyContent: "space-between",
//           alignItems: "center",
//           marginBottom: "10px",
//           flexWrap: "wrap",
//           gap: "10px",
//         }}
//       >
//         <h2 className="ppl-title" style={{ margin: 0 }}>
//           Project Progress List
//         </h2>

//         <div style={{ display: "flex", gap: "8px" }}>
//           <button
//             onClick={() => setShowFilters((prev) => !prev)}
//             style={{
//               backgroundColor: "#687c9a",
//               color: "#fff",
//               border: "none",
//               padding: "8px 12px",
//               borderRadius: "10px",
//               cursor: "pointer",
//               transition: "all 0.25s ease",
//               boxShadow: "0 4px 12px rgba(124, 58, 237, 0.25)",
//               fontSize: "13px",
//               fontWeight: "500",
//               minWidth: "140px",
//             }}
//           >
//             {showFilters ? "Hide Filters ▲" : "Show Filters ▼"}
//           </button>

//           <button onClick={handleDownloadExcel} className="download-btn">
//             ⬇ Download Excel
//           </button>
//         </div>
//       </div>

//       {/* ================= FILTERS ================= */}

//       {showFilters && (
//         <div className="ppd-filter-box">
//           {/* Org ID */}
//           <div className="ppd-field">
//             <label>Organisation ID</label>
//             <input value={user?.organisationId || ""} disabled />
//           </div>

//           {/* Type */}
//           <div className="ppd-field">
//             <label>Type</label>
//             <select
//               value={filters.type}
//               onChange={(e) => handleChange("type", e.target.value)}
//               key={dropdownData.types.join(",")}
//             >
//               <option value="All">All</option>
//               {(dropdownData.types || []).map((t, i) => (
//                 <option key={i} value={t}>
//                   {t}
//                 </option>
//               ))}
//             </select>
//           </div>

//           {/* Client - Multi Select */}
//           <div className="ppd-field">
//             <label>Client</label>
//             <SearchableMultiSelect
//               value={filters.client}
//               onChange={(value) => handleChange("client", value)}
//               options={dropdownData.clients || []}
//               placeholder="Select Clients..."
//               searchPlaceholder="Search clients..."
//               noResultsText="No clients found"
//             />
//           </div>

//           {/* Project - Multi Select */}
//           <div className="ppd-field">
//             <label>Project</label>
//             <SearchableMultiSelect
//               value={filters.project}
//               onChange={(value) => handleChange("project", value)}
//               options={dropdownData.projects || []}
//               placeholder="Select Projects..."
//               searchPlaceholder="Search projects..."
//               noResultsText="No projects found"
//             />
//           </div>

//           {/* Resource/Assigned To - Multi Select */}
//           <div className="ppd-field">
//             <label>Assigned To</label>
//             <SearchableMultiSelect
//               value={filters.resource}
//               onChange={(value) => handleChange("resource", value)}
//               options={dropdownData.resources || []}
//               placeholder="Select Resources..."
//               searchPlaceholder="Search resources..."
//               noResultsText="No resources found"
//             />
//           </div>

//           <div className="ppd-field">
//             <label>Due Date</label>
//             <input
//               type="date"
//               value={filters.dueDate}
//               onChange={(e) => handleChange("dueDate", e.target.value)}
//             />
//           </div>

//           {/* Submitted By - Multi Select */}
//           <div className="ppd-field">
//             <label>Submitted By</label>
//             <SearchableMultiSelect
//               value={filters.submittedBy}
//               onChange={(value) => handleChange("submittedBy", value)}
//               options={dropdownData.submittedBy || []}
//               placeholder="Select Users..."
//               searchPlaceholder="Search users..."
//               noResultsText="No users found"
//             />
//           </div>

//           {/* Due Status - Multi Select */}
//           <div className="ppd-field">
//             <label>Due Status</label>
//             <SearchableMultiSelect
//               value={filters.dueStatus}
//               onChange={(value) => handleChange("dueStatus", value)}
//               options={dropdownData.dueStatuses || []}
//               placeholder="Select Due Statuses..."
//               searchPlaceholder="Search statuses..."
//               noResultsText="No statuses found"
//             />
//           </div>

//           {/* Status - Multi Select */}
//           <div className="ppd-field">
//             <label>Status</label>
//             <SearchableMultiSelect
//               value={filters.status}
//               onChange={(value) => handleChange("status", value)}
//               options={dropdownData.statuses || []}
//               placeholder="Select Statuses..."
//               searchPlaceholder="Search statuses..."
//               noResultsText="No statuses found"
//             />
//           </div>

//           {/* Active Status Filter */}
//           <div className="ppd-field">
//             <label>Active Status</label>
//             <select
//               value={filters.active}
//               onChange={(e) => handleChange("active", e.target.value)}
//             >
//               <option value="All">All</option>
//               <option value="Active">Active</option>
//               <option value="Inactive">Inactive</option>
//             </select>
//           </div>

//           <div className="ppd-date-row">
//             <div className="ppd-field">
//               <label>Created From Date</label>
//               <input
//                 type="date"
//                 value={filters.fromDate}
//                 onChange={(e) => handleChange("fromDate", e.target.value)}
//               />
//             </div>

//             <div className="ppd-field">
//               <label>Created To Date</label>
//               <input
//                 type="date"
//                 value={filters.toDate}
//                 onChange={(e) => handleChange("toDate", e.target.value)}
//               />
//             </div>
//           </div>

//           {/* Changed Date Row */}
//           <div className="ppd-date-row">
//             <div className="ppd-field">
//               <label>Changed From Date</label>
//               <input
//                 type="date"
//                 value={filters.changedFromDate}
//                 onChange={(e) =>
//                   handleChange("changedFromDate", e.target.value)
//                 }
//               />
//             </div>

//             <div className="ppd-field">
//               <label>Changed To Date</label>
//               <input
//                 type="date"
//                 value={filters.changedToDate}
//                 onChange={(e) => handleChange("changedToDate", e.target.value)}
//               />
//             </div>
//           </div>

//           <div className="ppd-buttons">
//             <button className="apply-btn" onClick={() => handleApply(true)}>
//               Apply Filters
//             </button>
//             <button className="clear-btn" onClick={handleClear}>
//               Clear All
//             </button>
//           </div>
//         </div>
//       )}

//       {/* ================= TABLE WITH LOADER ================= */}
//       <div className="ppl-table-container">
//         {isLoading ? (
//           <div
//             style={{
//               display: "flex",
//               justifyContent: "center",
//               alignItems: "center",
//               minHeight: "400px",
//               flexDirection: "column",
//               gap: "20px",
//             }}
//           >
//             <div
//               style={{
//                 width: "50px",
//                 height: "50px",
//                 border: "4px solid #f3f3f3",
//                 borderTop: "4px solid #687c9a",
//                 borderRadius: "50%",
//                 animation: "spin 1s linear infinite",
//               }}
//             ></div>
//             <div
//               style={{ color: "#687c9a", fontSize: "16px", fontWeight: "500" }}
//             >
//               Loading project progress data...
//             </div>
//             <style>
//               {`
//                 @keyframes spin {
//                   0% { transform: rotate(0deg); }
//                   100% { transform: rotate(360deg); }
//                 }
//               `}
//             </style>
//           </div>
//         ) : (
//           <table className="ppl-table">
//             <thead>
//               <tr>
//                 <th>Active</th>
//                 <th>Client Name</th>
//                 <th>Project ID</th>
//                 <th style={{ width: "180px" }}>Project Name</th>
//                 <th>Project Manager</th>
//                 <th>Assigned To</th>
//                 <th>Due Date</th>
//                 <th>Due Status</th>
//                 <th
//                   onClick={() => handleSortToggle("createdOn")}
//                   style={{
//                     cursor: "pointer",
//                     userSelect: "none",
//                     whiteSpace: "nowrap",
//                   }}
//                   title="Click to sort by Created Date"
//                 >
//                   Created On{sortArrow("createdOn")}
//                 </th>
//                 <th
//                   onClick={() => handleSortToggle("chargedOn")}
//                   style={{
//                     cursor: "pointer",
//                     userSelect: "none",
//                     whiteSpace: "nowrap",
//                   }}
//                   title="Click to sort by Changed Date"
//                 >
//                   Changed On{sortArrow("chargedOn")}
//                 </th>
//                 <th>Status</th>
//                 <th>Priority</th>
//                 <th>Ticket/Task ID</th>
//                 <th style={{ width: "220px" }}>Title</th>
//                 <th>Type</th>
//                 <th>Submitted By</th>
//               </tr>
//             </thead>

//             <tbody>
//               {sortedData.length > 0 ? (
//                 sortedData.map((row, index) => (
//                   <tr key={index}>
//                     <td>
//                       <span
//                         style={{
//                           display: "inline-block",
//                           padding: "4px 10px",
//                           borderRadius: "20px",
//                           fontSize: "12px",
//                           fontWeight: "600",
//                           backgroundColor: row.active ? "#4caf50" : "#f44336",
//                           color: "#fff",
//                           textAlign: "center",
//                           minWidth: "60px",
//                         }}
//                       >
//                         {row.active ? "Active" : "Inactive"}
//                       </span>
//                     </td>
//                     <td>{row.clientDisplayName || "Not specified"}</td>
//                     <td>{row.projectId || "Not specified"}</td>
//                     <td
//                       style={{
//                         maxWidth: "180px",
//                         whiteSpace: "nowrap",
//                         overflow: "hidden",
//                         textOverflow: "ellipsis",
//                       }}
//                     >
//                       {row.projectId ? (
//                         <a
//                           href={`/project/${row.projectId}`}
//                           target="_blank"
//                           rel="noopener noreferrer"
//                           title="Click to view project details"
//                           style={{
//                             color: "#03275c",
//                             textDecoration: "none",
//                             cursor: "pointer",
//                           }}
//                           onMouseEnter={(e) =>
//                             (e.target.style.textDecoration = "underline")
//                           }
//                           onMouseLeave={(e) =>
//                             (e.target.style.textDecoration = "none")
//                           }
//                         >
//                           {row.projectName || row.projectId || "Not specified"}
//                         </a>
//                       ) : (
//                         "Not specified"
//                       )}
//                     </td>
//                     <td>{row.projectManagerName || "Not specified"}</td>
//                     <td>{row.resourceId || "Not specified"}</td>
//                     <td>
//                       {row.dueDate
//                         ? new Date(row.dueDate).toLocaleDateString()
//                         : "Not specified"}
//                     </td>
//                     <td>{row.dueStatus || "Not specified"}</td>
//                     <td>
//                       {row.createdOn
//                         ? new Date(row.createdOn).toLocaleDateString()
//                         : "Not specified"}
//                     </td>
//                     <td>
//                       {row.chargedOn
//                         ? new Date(row.chargedOn).toLocaleDateString()
//                         : "Not specified"}
//                     </td>
//                     <td>{row.status || "Not specified"}</td>
//                     <td>{row.priority || "Not specified"}</td>
//                     <td>
//                       {row.ticketId ? (
//                         row.type?.toLowerCase() === "task" ? (
//                           <a
//                             href={`/project/${row.projectId}/task/${row.ticketId}`}
//                             target="_blank"
//                             rel="noopener noreferrer"
//                             title="Click to view task details"
//                             style={{
//                               color: "#03275c",
//                               textDecoration: "none",
//                               cursor: "pointer",
//                             }}
//                             onMouseEnter={(e) =>
//                               (e.target.style.textDecoration = "underline")
//                             }
//                             onMouseLeave={(e) =>
//                               (e.target.style.textDecoration = "none")
//                             }
//                           >
//                             {row.ticketId}
//                           </a>
//                         ) : (
//                           <Link
//                             to={`/ticketinfo/${row.ticketId}`}
//                             target="_blank"
//                             rel="noopener noreferrer"
//                             title="Click to view ticket details"
//                             style={{
//                               color: "#03275c",
//                               textDecoration: "none",
//                               cursor: "pointer",
//                             }}
//                             onMouseEnter={(e) =>
//                               (e.target.style.textDecoration = "underline")
//                             }
//                             onMouseLeave={(e) =>
//                               (e.target.style.textDecoration = "none")
//                             }
//                           >
//                             {row.ticketId}
//                           </Link>
//                         )
//                       ) : (
//                         "Not specified"
//                       )}
//                     </td>
//                     <td
//                       style={{
//                         maxWidth: "220px",
//                         whiteSpace: "nowrap",
//                         overflow: "hidden",
//                         textOverflow: "ellipsis",
//                       }}
//                       title={row.title}
//                     >
//                       {row.title || "Not specified"}
//                     </td>
//                     <td>{row.type || "Not specified"}</td>
//                     <td>{row.submittedByName || "Not specified"}</td>
//                   </tr>
//                 ))
//               ) : (
//                 <tr>
//                   <td
//                     colSpan="17"
//                     className="no-data"
//                     style={{ textAlign: "center", padding: "40px" }}
//                   >
//                     No Data Found
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProjectProgressList;
